import React, { useState } from 'react';
import { Receipt, Run } from '../types';

interface ExpensePackButtonProps {
  run: Run;
  receipt?: Receipt;
  disabled?: boolean;
}

export const ExpensePackButton: React.FC<ExpensePackButtonProps> = ({
  run,
  receipt,
  disabled = false,
}) => {
  const [generating, setGenerating] = useState(false);

  const generateExpensePack = async (format: 'pdf' | 'csv') => {
    if (!receipt) {
      alert('No receipt available for this run.');
      return;
    }

    try {
      setGenerating(true);

      // Create expense data
      const expenseData = {
        runId: run.id,
        runTitle: run.title,
        organizer: run.createdBy,
        createdAt: run.createdAt,
        receipt: {
          vendor: receipt.vendor || 'Unknown Vendor',
          total: receipt.total || 0,
          date: receipt.date || run.createdAt,
          imageUrl: receipt.imageUrl,
        },
        orders: run.orders || [],
        totalOrders: run.orders?.length || 0,
        totalAmount: receipt.total || 0,
      };

      if (format === 'pdf') {
        await generatePDF(expenseData);
      } else {
        await generateCSV(expenseData);
      }
    } catch (error) {
      console.error('Error generating expense pack:', error);
      alert('Failed to generate expense pack. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const generatePDF = async (data: any) => {
    // Create a simple PDF-like text content for now
    // In a real implementation, you'd use a library like jsPDF
    const content = `
COFFEE RUN EXPENSE REPORT
========================

Run Details:
- Title: ${data.runTitle}
- Organizer: ${data.organizer}
- Date: ${new Date(data.createdAt).toLocaleDateString()}
- Run ID: ${data.runId}

Receipt Information:
- Vendor: ${data.receipt.vendor}
- Total Amount: $${data.receipt.total.toFixed(2)}
- Receipt Date: ${new Date(data.receipt.date).toLocaleDateString()}

Order Summary:
- Total Orders: ${data.totalOrders}
- Total Amount: $${data.totalAmount.toFixed(2)}

Orders:
${data.orders
  .map(
    (order: any, index: number) =>
      `${index + 1}. ${order.item} - ${order.customerName} - $${order.price?.toFixed(2) || '0.00'}`
  )
  .join('\n')}

Generated on: ${new Date().toLocaleString()}
    `.trim();

    // Create and download file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coffee-run-expense-${data.runId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateCSV = async (data: any) => {
    const csvContent = [
      ['Coffee Run Expense Report'],
      [''],
      ['Run Information'],
      ['Field', 'Value'],
      ['Title', data.runTitle],
      ['Organizer', data.organizer],
      ['Date', new Date(data.createdAt).toLocaleDateString()],
      ['Run ID', data.runId],
      [''],
      ['Receipt Information'],
      ['Field', 'Value'],
      ['Vendor', data.receipt.vendor],
      ['Total Amount', `$${data.receipt.total.toFixed(2)}`],
      ['Receipt Date', new Date(data.receipt.date).toLocaleDateString()],
      [''],
      ['Order Details'],
      ['Order #', 'Item', 'Customer', 'Price'],
      ...data.orders.map((order: any, index: number) => [
        index + 1,
        order.item,
        order.customerName,
        `$${order.price?.toFixed(2) || '0.00'}`,
      ]),
      [''],
      ['Summary'],
      ['Total Orders', data.totalOrders],
      ['Total Amount', `$${data.totalAmount.toFixed(2)}`],
    ];

    const csvString = csvContent
      .map(row => row.map((cell: any) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coffee-run-expense-${data.runId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isDisabled = disabled || generating || !receipt;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">Generate Expense Pack</p>

      <div className="flex space-x-2">
        <button
          onClick={() => generateExpensePack('pdf')}
          disabled={isDisabled}
          className={`
            flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors
            ${
              isDisabled
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:ring-offset-2'
            }
          `}
        >
          {generating ? '⏳' : '📄'} Text Report
        </button>

        <button
          onClick={() => generateExpensePack('csv')}
          disabled={isDisabled}
          className={`
            flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors
            ${
              isDisabled
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:ring-offset-2'
            }
          `}
        >
          {generating ? '⏳' : '📊'} CSV Export
        </button>
      </div>

      {!receipt && (
        <p className="text-xs text-gray-500">
          Upload a receipt to generate expense pack
        </p>
      )}

      {generating && (
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-coffee-600"></div>
          <span>Generating expense pack...</span>
        </div>
      )}
    </div>
  );
};

export default ExpensePackButton;
